import { isWhitespace, isWordChar, isAlphanumeric } from "@/services/multi-agent";

// ── Entity extraction helpers ──────────────────────────────────────────────

const FU_PREFIXES = [
  "apex_blast_radius", "auto_fix", "report", "weak_test_assertions",
  "integration_reuse_audit", "cleanup_delete_audit", "permission_security_audit",
  "report_dashboard_field_impact", "export markdown", "export pdf", "export word/docx",
];

const FU_ENTITY_SUFFIXES = [
  "Controller", "Trigger", "Handler", "Service", "Batch", "Helper", "Test", "Ctrl",
  "Manager", "Resource", "Selector", "Repository", "Wrapper", "Remoter", "Export",
];

function stripFollowUpPrefix(name: string): string {
  const lower = name.toLowerCase();
  for (const p of FU_PREFIXES) {
    if (lower.startsWith(p)) {
      let i = p.length;
      while (i < name.length && isWhitespace(name[i])) i++;
      if (name[i] === ":") i++;
      while (i < name.length && isWhitespace(name[i])) i++;
      return name.slice(i);
    }
  }
  return name;
}

function isFollowUpEntityToken(token: string): boolean {
  const first = token[0];
  const startsLetter = (first >= "a" && first <= "z") || (first >= "A" && first <= "Z");
  if (startsLetter && (token.endsWith("__c") || token.endsWith("__r"))) return true;
  if (first >= "A" && first <= "Z") {
    for (const suf of FU_ENTITY_SUFFIXES) {
      let idx = token.indexOf(suf);
      while (idx >= 1) {
        let alnumBefore = true;
        for (let k = 0; k < idx; k++) {
          if (!isAlphanumeric(token[k])) { alnumBefore = false; break; }
        }
        if (alnumBefore) return true;
        idx = token.indexOf(suf, idx + 1);
      }
    }
  }
  return false;
}

export function extractFollowUpEntities(md: string, artifacts: { name?: string }[]): string[] {
  const counts = new Map<string, number>();
  const add = (s?: string) => { if (s && s.length > 3) counts.set(s, (counts.get(s) || 0) + 1); };
  const harvest = (text: string) => {
    const t = text || "";
    let i = 0;
    while (i < t.length) {
      if (!isWordChar(t[i])) { i++; continue; }
      let j = i;
      while (j < t.length && isWordChar(t[j])) j++;
      const token = t.slice(i, j);
      i = j;
      if (isFollowUpEntityToken(token)) add(token);
    }
  };
  for (const a of artifacts) harvest(stripFollowUpPrefix(a.name || ""));
  harvest(md);
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k).slice(0, 3);
}

// ── Lane-aware follow-up suggestions ──────────────────────────────────────

/**
 * EXPORTED for the v1 conversation, which reuses this catalogue rather than copying it.
 *
 * v1 passes `lane: null` deliberately: the protocol's lane vocabulary is `reactive|planner|config`
 * and the cases below are legacy intent buckets (`apex_change`, `risk_audit`, …). Mapping one onto
 * the other would be a guess, so v1 takes the `default` branch and keeps the entity-derived
 * questions above it, which are the part that actually reflects the answer.
 */
export function followUpsForLane(lane: string | null, entities: string[]): {
  label: string; hint: string; question: string;
}[] {
  /*
   * `topic` EXISTS SO A GENERIC CHIP CANNOT SHADOW THE SPECIFIC ONE ABOVE IT.
   *
   * Seen in the browser on a plan-proposal run: "Callers of Approval_Region__c" and "Show callers"
   * side by side. Both ask who calls something; the second asks it vaguely ("the main classes
   * mentioned in this answer") about the very entity the first already names. The list was
   * `[...ctxQs, ...base].slice(0, 4)`, so whenever three entity chips were derived, the fourth slot
   * went to a generic chip that was, by construction, a worse restatement of the first.
   *
   * Tagging both halves by what they ASK — not by their wording — lets the specific one win and
   * the slot go to a question the reader has not already been offered. Untagged entries (lane
   * questions with no entity equivalent, e.g. "Group by object") never collide and always survive.
   */
  const ctxQs = entities.slice(0, 2).flatMap((n, i) =>
    i === 0
      ? [
          { topic: "callers", label: `Callers of ${n}`, hint: "Trace what invokes this", question: `Who calls ${n} and how often?` },
          { topic: "tests", label: `Tests for ${n}`, hint: "Coverage & gaps", question: `What tests cover ${n} and where are the coverage gaps?` },
        ]
      : [
          { topic: "blast", label: `Blast radius of ${n}`, hint: "Full impact tree", question: `What is the full blast radius of changing ${n}?` },
        ],
  );

  const base = (() => {
    switch (lane) {
      case "apex_change":
        return [
          { topic: "blast", label: "Blast radius",      hint: "Full impact tree",   question: "What's the full blast radius of this change?" },
          { topic: "tests", label: "Test coverage",     hint: "Coverage gaps",      question: "Which tests cover this and where are the coverage gaps?" },
          { label: "Same-object flows", hint: "Related automation", question: "What flows or triggers run against the same object?" },
        ];
      case "risk_audit":
        return [
          { label: "Highest severity",  hint: "Sort & summarize",   question: "Show me the 3 highest-severity items with concrete examples." },
          { label: "Group by object",   hint: "Reorganize",         question: "Re-group the findings by the SObject they affect." },
          { label: "Apex-only risks",   hint: "Narrow scope",       question: "Show me only the Apex risks — drop everything else." },
        ];
      case "flow_change":
        return [
          { label: "Trigger order",     hint: "Pre-flow triggers",  question: "What triggers run before this flow and could conflict?" },
          { label: "Field references",  hint: "Schema deps",        question: "Which fields and validation rules does this flow rely on?" },
        ];
      case "data_model":
        return [
          { label: "Field references",  hint: "Where it's used",    question: "Where else are these fields referenced — code, flows, validations?" },
          { label: "FLS / sharing",     hint: "Permissions impact", question: "Are there FLS or sharing rule implications from this change?" },
        ];
      default:
        return [
          { topic: "callers", label: "Show callers",      hint: "Inbound calls",      question: "Who calls the main classes mentioned in this answer?" },
          { topic: "tests", label: "Show tests",        hint: "Test coverage",      question: "What tests cover the classes mentioned?" },
          { label: "Recent changes",    hint: "Git history-ish",    question: "What's changed recently in the classes mentioned?" },
        ];
    }
  })();

  // Entity-derived first, so a specific chip claims its topic before the generic one is considered.
  const seen = new Set<string>();
  return [...ctxQs, ...base]
    .filter((q) => {
      const topic = (q as { topic?: string }).topic;
      if (!topic) return true;
      if (seen.has(topic)) return false;
      seen.add(topic);
      return true;
    })
    .slice(0, 4)
    .map(({ label, hint, question }) => ({ label, hint, question }));
}
